import { gql, useLazyQuery, useMutation } from '@apollo/client'
import {
  ActivateCampaignParams,
  ActivateCampaignResponse,
  AdCampaignData,
  AdParams,
  AdsvcGetRetailerCampaignResponse,
  CallsToAction,
  ChannelCampaign,
  Mutation,
  Query,
  RetailerAdCreativeTemplate,
} from '@promoboxx/graphql-gateway-types'
import { useCallback } from 'react'

const GET_CHANNEL_CAMPAIGN = gql`
  query GetChannelCampaign(
    $channel_campaign_id: ID!
    $params: ChannelCampaignParams!
  ) {
    getChannelCampaign(
      channel_campaign_id: $channel_campaign_id
      params: $params
    ) {
      id
      ad_set_templates {
        id
        channel_campaign_id
        targeting {
          age_min
          age_max
          genders
          interests {
            id
            name
          }
          platforms
          optimization_goal
          facebook_positions
          instagram_positions
          messenger_positions
        }
        optimization_goal
      }
      adcreative_templates {
        id
        brand_id
        creative_type
        creative {
          child_attachments {
            id
            link {
              link_type
              url
              append
              allow_retailer_override
            }
            media {
              media_type
              source_type
              source_id
              image_crop_list {
                crop_name
                left_top
                right_bottom
              }
            }
            name
            description
            call_to_action {
              cta_type
              link {
                link_type
                url
                append
              }
            }
          }
          media {
            media_type
            source_type
            source_id
            image_crop_list {
              crop_name
              left_top
              right_bottom
            }
          }
          message
          description
          link {
            link_type
            url
            append
            allow_retailer_override
          }
          call_to_action {
            cta_type
          }
          name
          created_at
          updated_at
          allow_url_override
          allow_call_to_action
          multi_share_end_card
          multi_share_optimized
        }
        allow_url_override
        allow_call_to_action_override
      }
      objective
      special_ad_categories
    }
  }
`

const GET_RETAILER_AD_CREATIVE_TEMPLATE = gql`
  query GetRetailerAdCreativeTemplate(
    $params: AdCreativeTemplateParams!
    $ad_creative_template_id: ID!
  ) {
    getRetailerAdCreativeTemplate(
      params: $params
      ad_creative_template_id: $ad_creative_template_id
    ) {
      name
      object_story_spec {
        page_id
        instagram_actor_id
        link_data {
          name
          caption
          description
          call_to_action {
            type
            value {
              link
            }
          }
          link
          message
          picture
          image_crop_list {
            crop_name
            left_top
            right_bottom
          }
          child_attachments {
            cloudinary_id
          }
        }
        video_data {
          cloudinary_id
          link_description
          message
          title
        }
      }
    }
  }
`

const GET_CALLS_TO_ACTION = gql`
  query GetCallsToAction($params: CallsToActionParams!) {
    getCallsToAction(params: $params) {
      BOOK_TRAVEL {
        code
        value
        status
      }
      DOWNLOAD {
        code
        value
        status
      }
      GET_QUOTE {
        code
        value
        status
      }
      GET_SHOWTIMES {
        code
        value
        status
      }
      LEARN_MORE {
        code
        value
        status
      }
      LISTEN_NOW {
        code
        value
        status
      }
      SHOP_NOW {
        code
        value
        status
      }
      SIGN_UP {
        code
        value
        status
      }
      SUBSCRIBE {
        code
        value
        status
      }
      WATCH_MORE {
        code
        value
        status
      }
    }
  }
`

const ACTIVATE_CAMPAIGN = gql`
  mutation ActivateCampaign(
    $params: ActivateCampaignParams!
    $campaignId: ID!
  ) {
    activateCampaign(params: $params, campaignId: $campaignId) {
      id
    }
  }
`

const GET_AD_CAMPAIGN = gql`
  query GetAdCampaign($params: AdParams!) {
    getAd(params: $params) {
      id
      state
      starts_at
      ends_at
      fb_ad_account_id
      thumbnail {
        source_id
      }
      type
      title
      campaign_channels {
        id
      }
      automated
      invitations_total_cents
      invitations {
        id
      }
      channel_campaign {
        id
        objective
        ad_set_templates {
          id
          targeting {
            age_min
            age_max
            genders
            interests {
              id
              name
            }
            platforms
            optimization_goal
            facebook_positions
            instagram_positions
            messenger_positions
          }
          optimization_goal
        }
        adcreative_templates {
          id
          creative_type
          allow_url_override
          allow_call_to_action_override
          creative {
            child_attachments {
              id
              media {
                source_type
                source_id
                media_type
                image_crop_list {
                  crop_name
                  left_top
                  right_bottom
                }
              }
              link {
                link_type
                url
                append
                allow_retailer_override
              }
              call_to_action {
                cta_type
                link {
                  link_type
                  url
                  append
                  allow_retailer_override
                }
              }
              title
              url
              name
              description
            }
            media {
              source_type
              source_id
              media_type
              image_crop_list {
                crop_name
                left_top
                right_bottom
              }
            }
            link {
              link_type
              url
              append
              allow_retailer_override
            }
            call_to_action {
              cta_type
              link {
                link_type
                url
                append
                allow_retailer_override
              }
            }
            name
            description
            message
            allow_url_override
            allow_call_to_action
            multi_share_end_card
            multi_share_optimized
          }
        }
      }
    }
  }
`

const GET_PAID_AD = gql`
  query GetRetailerPaidAdCampaign(
    $adsvcGetRetailerCampaignId: ID!
    $retailerId: Int!
  ) {
    adsvcGetRetailerCampaign(
      id: $adsvcGetRetailerCampaignId
      retailerID: $retailerId
    ) {
      id
      brandId
      objective
      state
      startsAt
      endsAt
      title
      thumbnail {
        sourceType
        sourceID
      }
      campaignChannels {
        id
        campaignID
        channel
        channelCampaignID
        createdAt
      }
      campaignInvitation {
        id
        campaignID
        retailerID
        businessID
        state
        intendedAmountCents
        createdAt
        updatedAt
        refundedAt
        refundAttempts
        acceptedAt
        automated
        automateAt
        failureReason
      }
      automated
      automatedNow
      platforms
      createdAt
      publishedAt
      type
      fbAdAccountId
    }
  }
`

export function usePaidAds() {
  const [getChannelCampaignQuery] = useLazyQuery<Query>(GET_CHANNEL_CAMPAIGN)
  const [getRetailerAdCreativeTemplateQuery] = useLazyQuery<Query>(
    GET_RETAILER_AD_CREATIVE_TEMPLATE,
  )
  const [getCallsToActionQuery] = useLazyQuery<Query>(GET_CALLS_TO_ACTION)
  const [activateCampaignMutation] = useMutation<Mutation>(ACTIVATE_CAMPAIGN)
  const [getAdCampaignDataQuery] = useLazyQuery<Query>(GET_AD_CAMPAIGN)
  const [getPaidAdQuery] = useLazyQuery<Query>(GET_PAID_AD)

  const getChannelCampaign = useCallback(
    async (
      channelCampaignId: string,
      retailerId: string,
    ): Promise<ChannelCampaign | null> => {
      return await getChannelCampaignQuery({
        variables: {
          params: {
            retailer_id: retailerId,
          },
          channel_campaign_id: channelCampaignId,
        },
      })
        .then((response) => {
          return response?.data?.getChannelCampaign || null
        })
        .catch((error) => {
          return error
        })
    },
    [getChannelCampaignQuery],
  )

  const getRetailerAdCreativeTemplate = useCallback(
    async (
      retailerId: string,
      adCreativeTemplateId: string,
      facebookPageId?: string,
    ): Promise<RetailerAdCreativeTemplate | null> => {
      return await getRetailerAdCreativeTemplateQuery({
        variables: {
          params: {
            retailer_id: retailerId,
            facebook_page_id: facebookPageId,
          },
          ad_creative_template_id: adCreativeTemplateId,
        },
      })
        .then((response) => {
          return response?.data?.getRetailerAdCreativeTemplate || null
        })
        .catch((error) => {
          return error
        })
    },
    [getRetailerAdCreativeTemplateQuery],
  )

  const getCallsToAction =
    useCallback(async (): Promise<CallsToAction | null> => {
      return await getCallsToActionQuery({
        variables: {
          params: {
            objective: 'LINK_CLICKS',
          },
        },
      })
        .then((response) => {
          return response?.data?.getCallsToAction || null
        })
        .catch((error) => {
          return error
        })
    }, [getCallsToActionQuery])

  const activateCampaign = useCallback(
    async (
      params: ActivateCampaignParams,
      campaignId: string,
      retailerId: string,
      channelCampaignId: string,
    ): Promise<ActivateCampaignResponse | null> => {
      return await activateCampaignMutation({
        variables: {
          params,
          campaignId,
        },
        refetchQueries: [
          {
            query: GET_CHANNEL_CAMPAIGN,
            variables: {
              params: {
                retailer_id: retailerId,
              },
              channel_campaign_id: channelCampaignId,
            },
          },
        ],
      }).then((response) => {
        return response?.data?.activateCampaign || null
      })
    },
    [activateCampaignMutation],
  )

  const getAdCampaignData = useCallback(
    async (params: AdParams): Promise<AdCampaignData | null> => {
      return await getAdCampaignDataQuery({
        variables: {
          params,
        },
      }).then((response) => {
        return response?.data?.getAd || null
      })
    },
    [getAdCampaignDataQuery],
  )

  const getPaidAd = useCallback(
    async (
      adsvcGetRetailerCampaignId: string,
      retailerId: number,
    ): Promise<AdsvcGetRetailerCampaignResponse | null> => {
      return await getPaidAdQuery({
        variables: {
          adsvcGetRetailerCampaignId,
          retailerId,
        },
      }).then((response) => {
        return response?.data?.adsvcGetRetailerCampaign || null
      })
    },
    [getPaidAdQuery],
  )

  return {
    getAdCampaignData,
    getChannelCampaign,
    getRetailerAdCreativeTemplate,
    getCallsToAction,
    activateCampaign,
    getPaidAd,
  }
}
